import axios from 'axios';
import { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const API_BASE = process.env.REACT_APP_API_URL || '';

function TenantLogin() {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);

  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { run: runSubmission, isPending } = useSubmissionLock();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || isPending('tenant-login')) return;

    await runSubmission('tenant-login', async () => {
      try {
        setLoading(true);
        setError('');
        const res = await axios.post(`${API_BASE}/api/tenant/login`, form, {
          withCredentials: true,
        });
        if (res.data?.token) {
          localStorage.setItem('accessToken', res.data.token);
        }
        setUser(res.data.user);
        navigate('/tenant-portal');
      } catch (err) {
        console.error('Login failed:', err.response?.data || err.message);
        setError(err.response?.data?.error || err.response?.data?.message || 'Login failed.');
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <div className="flex h-screen">
      {/* LEFT: Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b] p-8">
        <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-10 w-full max-w-md shadow-2xl">
          <div className="text-center mb-10">
            <img
              src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
              alt="Beem Logo"
              className="w-24 mx-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-white mb-2">Welcome back</h2>
            <p className="text-gray-400 text-sm">Log in to your tenant dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block mb-1 text-sm font-medium text-gray-300">
                Email address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg bg-white/15 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block mb-1 text-sm font-medium text-gray-300">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg bg-white/15 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center justify-between text-sm text-gray-400">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="accent-[#AF9B8A] rounded"
                />
                Remember me
              </label>
              <Link to="/forgot-password" className="hover:text-[#AF9B8A] transition-colors">
                Forgot password?
              </Link>
            </div>

            {error && <p className="text-red-400 text-center text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-4 bg-[#AF9B8A] hover:bg-[#9e8a78] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors"
              aria-busy={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="mt-8 text-center text-gray-400 text-sm">
            Don’t have an account? Contact your property manager.
          </p>
        </div>
      </div>

      {/* RIGHT: Illustration */}
      <div className="hidden md:block w-1/2 relative">
        <img
          src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/68e10ee19b260e7f43fddd20_ChatGPT%20Image%20Oct%204%2C%202025%2C%2012_26_41%20PM.png"
          alt="City illustration"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-black/20 to-transparent" />
      </div>
    </div>
  );
}

export default TenantLogin;
